import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Avatar,
  Box,
  Text,
  useColorModeValue,
  useToast,
  Divider,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '../../state/userAtom';

function ProfileModal({ isOpen, onClose }) {
  const user = useRecoilValue(userAtom);
  const setUser = useSetRecoilState(userAtom);
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setIsChangingPassword(false);
    }
  }, [user, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');

      const response = await fetch(`${serverUrl}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(prev => ({ ...prev, name: formData.name, email: formData.email }));
        toast({
          title: 'Profile updated',
          status: 'success',
          duration: 3000,
        });
      } else {
        throw new Error(data.error || 'Failed to update profile');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!formData.currentPassword) {
      toast({
        title: 'Current password is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (formData.newPassword.length < 6) {
      toast({
        title: 'New password must be at least 6 characters',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: 'Passwords do not match',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');

      const response = await fetch(`${serverUrl}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Password changed',
          description: 'Your password has been updated successfully.',
          status: 'success',
          duration: 3000,
        });
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
        setIsChangingPassword(false);
      } else {
        throw new Error(data.error || 'Failed to change password');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <ModalHeader>My Profile</ModalHeader>
        <ModalCloseButton borderRadius="full" />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Avatar Section */}
            <Box textAlign="center">
              <Avatar
                size="xl"
                name={user?.name || 'User'}
                bg="brand.500"
                color="white"
                mb={3}
              />
              <Text fontWeight="600" fontSize="lg">{user?.name || 'User'}</Text>
              <Text fontSize="sm" color={textSecondary}>{user?.email}</Text>
              <HStack justify="center" mt={2}>
                <Badge colorScheme={user?.role === 'admin' ? 'purple' : 'blue'} borderRadius="full">
                  {user?.role || 'user'}
                </Badge>
              </HStack>
            </Box>

            <Divider />

            {/* Profile Form */}
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  bg={inputBg}
                  borderRadius="xl"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  bg={inputBg}
                  borderRadius="xl"
                />
              </FormControl>

              <Button
                colorScheme="brand"
                onClick={handleSaveProfile}
                isLoading={isLoading && !isChangingPassword}
                borderRadius="xl"
              >
                Save Changes
              </Button>
            </VStack>

            <Divider />

            {/* Password Section */}
            {!isChangingPassword ? (
              <Button
                variant="outline"
                onClick={() => setIsChangingPassword(true)}
                borderRadius="xl"
              >
                Change Password
              </Button>
            ) : (
              <VStack spacing={4} align="stretch">
                <Text fontWeight="600" fontSize="sm">Change Password</Text>
                <FormControl>
                  <FormLabel fontSize="sm">Current Password</FormLabel>
                  <Input
                    name="currentPassword"
                    type="password"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    bg={inputBg}
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">New Password</FormLabel>
                  <Input
                    name="newPassword"
                    type="password"
                    value={formData.newPassword}
                    onChange={handleChange}
                    bg={inputBg}
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Confirm New Password</FormLabel>
                  <Input
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    bg={inputBg}
                    borderRadius="xl"
                  />
                </FormControl>

                <HStack>
                  <Button
                    flex={1}
                    variant="ghost"
                    onClick={() => setIsChangingPassword(false)}
                    borderRadius="xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    flex={1}
                    colorScheme="brand"
                    onClick={handleChangePassword}
                    isLoading={isLoading && isChangingPassword}
                    borderRadius="xl"
                  >
                    Update Password
                  </Button>
                </HStack>
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose} borderRadius="xl">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ProfileModal;
